import React, { FC, useContext, useEffect, useState } from 'react';
import cx from 'classnames';

import dayjs from 'dayjs';

import { MDText } from 'i18n-react';

import { timeoutFetchSentryGuard } from 'Fetch';
import { getRecentSales } from 'PublicAPI';

import { ConfigContext } from 'Context/config';

import { Content } from 'LegacySBCRA/Content/Content';
import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';

import type { RecentSale } from '../../../../../client/index.helper';
import { useBreakpoint } from '../../../../components/misc/Breakpoints/Breakpoints';
import { usePageRefEvents } from '../../common/Hooks/usePageRefEvents';
import { RoundedSection, RoundedSectionType } from '../../common/RoundedSection/RoundedSection';

import { Carousel } from './Carousel';
import { RecentSaleImage } from './RecentSaleImage';
import LocalTexts from './RecentSalesText.json';
import { RecentSaleTimeStamp } from './RecentSaleTimeStamp';

import styles from './RecentSales.scss';

const LocalT = new MDText(LocalTexts);

type InternalRecentSale = {
  id: string;
  img: { url: string };
  model: string;
  name: string;
  price: number;
  timestamp: dayjs.Dayjs;
};

type RecentSalesProps = {
  carouselClassName?: string;
  className?: string;
  onLoad?: () => void;
  theme?: RoundedSectionType;
};

const mapData = (data: RecentSale[]): InternalRecentSale[] =>
  data.map(({ sale, seller, vehicle }: RecentSale) => ({
    id: `${seller?.firstName}-${vehicle?.make}-${vehicle?.model}-${sale?.soldPrice}`.replace(
      /\s/g,
      '',
    ),
    img: { url: vehicle?.leadPhoto?.url },
    model: `${vehicle?.make} ${vehicle?.model}`,
    name: seller?.firstName,
    price: sale?.soldPrice,
    timestamp: dayjs(sale?.soldDate),
  })).sort((item1, item2) => item2.timestamp.valueOf() - item1.timestamp.valueOf());

const RecentSales: FC<RecentSalesProps> = ({
  carouselClassName,
  className,
  onLoad,
  theme,
}) => {
  const { configState: { recentSales } } = useContext(ConfigContext);

  const configRecentSalesData = Array.isArray(recentSales) ? mapData(recentSales) : [];
  const [recentSalesData, setRecentSalesData] = useState<InternalRecentSale[]>(configRecentSalesData);
  const [loading, setLoading] = useState(recentSalesData?.length === 0);
  const breakpoints = useBreakpoint();
  const { breakpointTabletMobile } = breakpoints.maxWidth;

  const { setRef } = usePageRefEvents({ onRefTrigger: onLoad });

  useEffect(() => {
    const loadRecentSales = async () => {
      if (process.env.NODE_ENV === 'production') {
        try {
          const recentSalesResponse: RecentSale[] = await getRecentSales();

          setLoading(false);
          if (Array.isArray(recentSalesResponse)) {
            setRecentSalesData(mapData(recentSalesResponse));
          } else {
            throw new Error(`Server response is not an array: ${recentSalesResponse}`);
          }
        } catch (err) {
          setLoading(false);
          timeoutFetchSentryGuard(err, () => {
            window?.Sentry?.captureException?.(new Error(err as string), {
              level: 'warning',
              tags: {
                fetch: 'recent sales',
              },
            });
          });
        }
      } else {
        import('./mockRecentSales').then(({ default: recentSalesMock }) => {
          setLoading(false);
          setRecentSalesData(mapData(recentSalesMock));
        });
      }
    };

    if (loading) {
      loadRecentSales();
    }
  }, [loading]);

  if (!loading && recentSalesData.length === 0) {
    return null;
  }

  return (
    <RoundedSection withBottomLedge withTopLedge theme={theme}>
      <div className={cx(className, carouselClassName, styles.component)}>
        <Content className={styles.container}>
          <h2>{LocalT.translate('title')}</h2>
        </Content>
        <div
          ref={(ref) => setRef(ref)}
          className={ cx(styles.container, styles.carousel) }
          data-testid="recentSales">
          { loading && (
            <div className={styles.loader}>
              <LoadingArrow showText size="large" text={`${LocalT.translate('loading')}`} />
            </div>
          )}
          <Carousel
            slideInterval={[1000, 2000]}
            vertical={breakpointTabletMobile}
            wrapperClassName={carouselClassName}
          >
            {recentSalesData.map((recentSale) => {
              let modelName: string | string[] = recentSale.model.trim();
              const imageSize = { height: 124, width: 137 };

              if (modelName.length >= 32) {
                modelName = modelName.substring(0, 32).split(' ');
                modelName.pop();
                modelName = `${modelName.join(' ')}…`;
              }

              return (
                <div key={recentSale.id} className={styles.item}>
                  <div className={styles.content}>
                    <div className={styles.name}>Sold by {recentSale.name}</div>
                    <div className={styles.description}>
                      {`${modelName} for £${recentSale.price.toLocaleString()}.`}
                    </div>
                    <RecentSaleTimeStamp timestamp={ dayjs(recentSale.timestamp).fromNow() } />
                    { recentSale?.img?.url && (
                      <RecentSaleImage
                        dimensions={ imageSize }
                        src={ recentSale.img.url }
                        vehicleModel={ recentSale.model } />)
                    }
                  </div>
                </div>
              );
            })}
          </Carousel>
        </div>
      </div>
    </RoundedSection>
  );
};

export default RecentSales;
